<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { RouterLink } from 'vue-router'
import { useDocumentTitle } from '@/composables/useDocumentTitle'
import { useFightersStore } from '@/stores/fighters'
import { useEventsStore } from '@/stores/events'
import FighterImage from '@/components/FighterImage.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import EmptyState from '@/components/EmptyState.vue'
import FighterCardSkeleton from '@/components/FighterCardSkeleton.vue'
import FeaturedEventSkeleton from '@/components/FeaturedEventSkeleton.vue'
import EventCardSkeleton from '@/components/EventCardSkeleton.vue'
import StatusIndicator from '@/components/StatusIndicator.vue'
import TooltipWrapper from '@/components/TooltipWrapper.vue'
import BadgeComponent from '@/components/BadgeComponent.vue'

useDocumentTitle('Home')

// Touch swipe state for carousel
const carouselRef = ref<HTMLElement | null>(null)
const touchStartX = ref(0)
const touchEndX = ref(0)
const isSwiping = ref(false)
const swipeThresholdPercent = 0.1 // 10% of container width to trigger a swipe

// Calculate minimum swipe distance based on container width (percentage-based for consistent UX across screen sizes)
const minSwipeDistance = computed(() => {
  const containerWidth = carouselRef.value?.offsetWidth || screenWidth.value
  return containerWidth * swipeThresholdPercent
})

// Responsive screen width tracking
const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth
}

// Responsive fighters per page: 1 on mobile, 2 on tablet, 3 on desktop
const fightersPerPage = computed(() => {
  if (screenWidth.value < 768) return 1  // Mobile
  if (screenWidth.value < 1024) return 2 // Tablet
  return 3                                // Desktop
})

const fightersStore = useFightersStore()
const eventsStore = useEventsStore()

const { fighters, isLoading: fightersLoading, error: fightersError } = storeToRefs(fightersStore)
const { events, isLoading: eventsLoading, error: eventsError } = storeToRefs(eventsStore)

// Consolidated error state when both requests fail
const hasBothErrors = computed(() => fightersError.value && eventsError.value)
const consolidatedErrorMessage = computed(() => {
  if (!hasBothErrors.value) return ''
  return 'Unable to connect to the server. Both fighters and events data could not be loaded.'
})

const { fetchFighters, clearError: clearFightersError } = fightersStore
const { fetchEvents, formatDate, clearError: clearEventsError } = eventsStore

const handleRetryFighters = () => {
  clearFightersError()
  fetchFighters()
}

const handleRetryEvents = () => {
  clearEventsError()
  fetchEvents()
}

const handleRetryAll = () => {
  clearFightersError()
  clearEventsError()
  fetchFighters()
  fetchEvents()
}

// Countdown timer state (updates every 10 seconds to reduce CPU/battery usage)
// Since countdowns are typically to events days/weeks away, 1-second precision is unnecessary
const countdown = ref({ days: 0, hours: 0, minutes: 0, seconds: 0 })
let countdownInterval: ReturnType<typeof setInterval> | null = null
const COUNTDOWN_INTERVAL_MS = 10000 // 10 seconds

// Screen reader announcement state (only updates on significant changes: days/hours change or key milestones)
const countdownAnnouncement = ref('')
let lastAnnouncedDays = -1
let lastAnnouncedHours = -1

// Get featured event (next upcoming event)
const featuredEvent = computed(() => {
  const upcoming = events.value
    .filter(e => e.status === 'upcoming')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  return upcoming[0] || null
})

// Get recent completed events with results
const recentResults = computed(() => {
  return events.value
    .filter(e => e.status === 'completed')
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3)
})

// Top ranked fighters (first 6 from the list)
const topFighters = computed(() => {
  return fighters.value.slice(0, 6)
})

// Fighter carousel state
const carouselIndex = ref(0)
const slideAnnouncement = ref('')

const visibleFighters = computed(() => {
  const start = carouselIndex.value * fightersPerPage.value
  return topFighters.value.slice(start, start + fightersPerPage.value)
})

// Calculate total number of slides
const totalSlides = computed(() => {
  return Math.ceil(topFighters.value.length / fightersPerPage.value)
})

// Current slide (1-indexed for display)
const currentSlide = computed(() => {
  return carouselIndex.value + 1
})

// Announce slide change to screen readers
const announceSlideChange = () => {
  const fighterNames = visibleFighters.value.map(f => f.name).join(', ')
  slideAnnouncement.value = `Slide ${currentSlide.value} of ${totalSlides.value}: Showing ${fighterNames}`
}

const canGoNext = computed(() => {
  return (carouselIndex.value + 1) * fightersPerPage.value < topFighters.value.length
})

const canGoPrev = computed(() => {
  return carouselIndex.value > 0
})

const nextSlide = () => {
  if (canGoNext.value) {
    carouselIndex.value++
    announceSlideChange()
  }
}

const prevSlide = () => {
  if (canGoPrev.value) {
    carouselIndex.value--
    announceSlideChange()
  }
}

// Navigate to a specific slide
const goToSlide = (index: number) => {
  if (index >= 0 && index < totalSlides.value) {
    carouselIndex.value = index
    announceSlideChange()
  }
}

// Touch swipe handlers for carousel
const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX
  isSwiping.value = true
}

const handleTouchMove = (e: TouchEvent) => {
  if (!isSwiping.value) return
  touchEndX.value = e.touches[0].clientX
}

const handleTouchEnd = () => {
  if (!isSwiping.value) return
  isSwiping.value = false

  const swipeDistance = touchStartX.value - touchEndX.value
  const threshold = minSwipeDistance.value

  // Swipe left (next slide)
  if (swipeDistance > threshold && canGoNext.value) {
    nextSlide()
  }
  // Swipe right (previous slide)
  else if (swipeDistance < -threshold && canGoPrev.value) {
    prevSlide()
  }

  // Reset touch positions
  touchStartX.value = 0
  touchEndX.value = 0
}

// Keyboard navigation for carousel
const handleCarouselKeydown = (e: KeyboardEvent) => {
  if (e.key === 'ArrowLeft') {
    e.preventDefault()
    prevSlide()
  } else if (e.key === 'ArrowRight') {
    e.preventDefault()
    nextSlide()
  }
}

// Accessible countdown label for screen readers
// Includes full context with event name and all time units
const countdownLabel = computed(() => {
  const { days, hours, minutes, seconds } = countdown.value
  const parts: string[] = []

  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
  if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`)

  if (parts.length === 0) return 'Event is starting now'

  const eventName = featuredEvent.value?.name || 'Event'
  return `${eventName} countdown: ${parts.join(', ')}`
})

// Calculate countdown to featured event
const updateCountdown = () => {
  if (!featuredEvent.value) return

  const eventDate = new Date(featuredEvent.value.date + 'T20:00:00')
  const now = new Date()
  const diff = eventDate.getTime() - now.getTime()

  if (diff <= 0) {
    countdown.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
    // Announce when countdown reaches zero (only once)
    if (lastAnnouncedDays !== 0 || lastAnnouncedHours !== 0) {
      countdownAnnouncement.value = 'Event is starting now!'
      lastAnnouncedDays = 0
      lastAnnouncedHours = 0
    }
    return
  }

  const newCountdown = {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  }

  countdown.value = newCountdown

  // Only announce to screen readers on significant changes to avoid overwhelming users:
  // - Initial load
  // - When days change
  // - When hours change
  // This means announcements happen at most once per hour, not every minute/second
  const daysChanged = newCountdown.days !== lastAnnouncedDays
  const hoursChanged = newCountdown.hours !== lastAnnouncedHours
  const isInitialAnnouncement = lastAnnouncedDays === -1

  if (isInitialAnnouncement || daysChanged || hoursChanged) {
    lastAnnouncedDays = newCountdown.days
    lastAnnouncedHours = newCountdown.hours

    // Build announcement text (only days and hours for significant changes)
    const parts: string[] = []
    if (newCountdown.days > 0) parts.push(`${newCountdown.days} ${newCountdown.days === 1 ? 'day' : 'days'}`)
    if (newCountdown.hours > 0) parts.push(`${newCountdown.hours} ${newCountdown.hours === 1 ? 'hour' : 'hours'}`)

    // Only include minutes if we're under 1 hour (more precision needed when close to event)
    if (newCountdown.days === 0 && newCountdown.hours === 0 && newCountdown.minutes > 0) {
      parts.push(`${newCountdown.minutes} ${newCountdown.minutes === 1 ? 'minute' : 'minutes'}`)
    }

    if (parts.length > 0) {
      countdownAnnouncement.value = `Event starts in ${parts.join(', ')}`
    }
  }
}

// Reset carousel index when screen size changes to prevent showing empty slides
const handleResize = () => {
  updateScreenWidth()
  // Reset to first slide if current index would be out of bounds
  if (carouselIndex.value >= totalSlides.value) {
    carouselIndex.value = Math.max(0, totalSlides.value - 1)
  }
}

onMounted(() => {
  fetchFighters()
  fetchEvents()
  updateCountdown()
  countdownInterval = setInterval(updateCountdown, COUNTDOWN_INTERVAL_MS)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  window.removeEventListener('resize', handleResize)
})

// Navigation cards data
const navigationCards = [
  {
    title: 'Fighters',
    description: 'Browse the best fighters on the planet',
    icon: '👊',
    route: 'fighters',
    color: 'bg-ufc-red'
  },
  {
    title: 'Events',
    description: 'Upcoming and past UFC events',
    icon: '🏆',
    route: 'events',
    color: 'bg-ufc-gold text-black'
  },
  {
    title: 'About',
    description: 'Learn more about this platform',
    icon: 'ℹ️',
    route: 'about',
    color: 'bg-gray-600'
  }
]
</script>

<template>
  <div class="home p-4 md:p-6 lg:p-8">
    <div class="max-w-6xl mx-auto">
      <!-- Hero Section -->
      <div class="text-center section-gap-lg">
        <h1 class="text-h1 text-ufc-gold-accessible section-gap-sm">
          UFC Website
        </h1>
        <p class="text-subtitle text-gray-300">
          The ultimate UFC experience - better than anything the world has ever seen.
        </p>
      </div>

      <!-- Global Error Message (when both fail) -->
      <ErrorMessage
        v-if="hasBothErrors"
        :message="consolidatedErrorMessage"
        :is-retrying="fightersLoading || eventsLoading"
        title="Unable to load data"
        variant="network"
        class="section-gap-lg"
        @retry="handleRetryAll"
      />

      <!-- Events Error (only when events fail but not both) -->
      <ErrorMessage
        v-if="eventsError && !hasBothErrors"
        :message="eventsError"
        :is-retrying="eventsLoading"
        title="Unable to load events"
        variant="warning"
        compact
        class="section-gap-md"
        @retry="handleRetryEvents"
      />

      <!-- Featured Event Loading Skeleton -->
      <section v-if="eventsLoading && !featuredEvent" class="section-gap-lg" aria-labelledby="featured-event-loading">
        <h2 id="featured-event-loading" class="sr-only">Loading Featured Event</h2>
        <FeaturedEventSkeleton />
      </section>

      <!-- Featured Upcoming Event with Countdown -->
      <section v-else-if="featuredEvent && !eventsError" class="section-gap-lg" aria-labelledby="featured-event-heading">
        <h2 id="featured-event-heading" class="sr-only">Featured Event</h2>
        <RouterLink
          :to="{ name: 'event-detail', params: { id: featuredEvent.id } }"
          class="block card card-interactive hover:ring-2 hover:ring-ufc-gold transition-all"
        >
          <div class="flex flex-col lg:flex-row lg:items-center gap-6">
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-3">
                <StatusIndicator :status="featuredEvent.status" />
                <span class="text-gray-300 text-sm">{{ featuredEvent.broadcast }}</span>
              </div>
              <h3 class="text-h2 text-white mb-2">{{ featuredEvent.name }}</h3>
              <p class="text-h3 text-ufc-gold-accessible mb-2">{{ featuredEvent.mainEvent }}</p>
              <p class="text-gray-300">
                {{ featuredEvent.venue }} - {{ featuredEvent.location }}
              </p>
              <p class="text-gray-300 mt-2">{{ formatDate(featuredEvent.date) }}</p>
            </div>

            <!-- Countdown Timer -->
            <div class="lg:text-right">
              <p class="text-sm text-gray-300 mb-2 uppercase tracking-wider" aria-hidden="true">Event starts in</p>
              <!-- Visually hidden live region for periodic screen reader announcements -->
              <div
                aria-live="polite"
                aria-atomic="true"
                class="sr-only"
              >
                {{ countdownAnnouncement }}
              </div>
              <div
                class="grid grid-cols-2 xs:flex gap-3 lg:justify-end"
                role="group"
                :aria-label="countdownLabel"
              >
                <div class="text-center" aria-hidden="true">
                  <div class="bg-gray-700 rounded-lg px-3 py-2 md:px-4 md:py-3 min-w-[60px]">
                    <span class="text-2xl md:text-3xl font-bold text-ufc-gold-accessible">{{ countdown.days }}</span>
                  </div>
                  <span class="text-xs text-gray-300 mt-1 block">Days</span>
                </div>
                <div class="text-center" aria-hidden="true">
                  <div class="bg-gray-700 rounded-lg px-3 py-2 md:px-4 md:py-3 min-w-[60px]">
                    <span class="text-2xl md:text-3xl font-bold text-ufc-gold-accessible">{{ countdown.hours }}</span>
                  </div>
                  <span class="text-xs text-gray-300 mt-1 block">Hours</span>
                </div>
                <div class="text-center" aria-hidden="true">
                  <div class="bg-gray-700 rounded-lg px-3 py-2 md:px-4 md:py-3 min-w-[60px]">
                    <span class="text-2xl md:text-3xl font-bold text-ufc-gold-accessible">{{ countdown.minutes }}</span>
                  </div>
                  <span class="text-xs text-gray-300 mt-1 block">Min</span>
                </div>
                <div class="text-center" aria-hidden="true">
                  <div class="bg-gray-700 rounded-lg px-3 py-2 md:px-4 md:py-3 min-w-[60px]">
                    <span class="text-2xl md:text-3xl font-bold text-ufc-gold-accessible">{{ countdown.seconds }}</span>
                  </div>
                  <span class="text-xs text-gray-300 mt-1 block">Sec</span>
                </div>
              </div>
            </div>
          </div>
        </RouterLink>
      </section>

      <!-- Quick Navigation Cards -->
      <section class="section-gap-lg" aria-labelledby="nav-heading">
        <h2 id="nav-heading" class="text-h2 text-white section-gap-sm">Explore</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RouterLink
            v-for="card in navigationCards"
            :key="card.route"
            :to="{ name: card.route }"
            class="card card-interactive hover:ring-2 hover:ring-ufc-gold transition-all flex items-center gap-4"
          >
            <div :class="[card.color, 'w-12 h-12 rounded-lg flex items-center justify-center text-2xl']" aria-hidden="true">
              {{ card.icon }}
            </div>
            <div>
              <h3 class="text-h4 text-white">{{ card.title }}</h3>
              <p class="text-sm text-gray-300">{{ card.description }}</p>
            </div>
          </RouterLink>
        </div>
      </section>

      <!-- Fighters Error (only when fighters fail but not both) -->
      <ErrorMessage
        v-if="fightersError && !hasBothErrors"
        :message="fightersError"
        :is-retrying="fightersLoading"
        title="Unable to load fighters"
        variant="warning"
        compact
        class="section-gap-md"
        @retry="handleRetryFighters"
      />

      <!-- Top Ranked Fighters Carousel -->
      <section
        v-if="!fightersError"
        ref="carouselRef"
        tabindex="0"
        class="section-gap-lg touch-pan-y focus-ring rounded-lg"
        role="region"
        aria-roledescription="carousel"
        aria-label="Top Ranked Fighters carousel. Use left and right arrow keys to navigate between slides."
        aria-labelledby="fighters-heading"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @keydown="handleCarouselKeydown"
      >
        <!-- Live region for slide change announcements -->
        <div
          aria-live="polite"
          aria-atomic="true"
          class="sr-only"
        >
          {{ slideAnnouncement }}
        </div>

        <div class="flex items-center justify-between section-gap-sm">
          <h2 id="fighters-heading" class="text-h2 text-white section-gap-sm">Top Ranked Fighters</h2>
          <div v-if="!fightersLoading" class="flex gap-2">
            <TooltipWrapper text="Previous" position="bottom">
              <button
                :disabled="!canGoPrev"
                :aria-disabled="!canGoPrev"
                class="btn btn-secondary btn-icon btn-sm"
                aria-label="Previous fighters"
                @click="prevSlide"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </TooltipWrapper>
            <TooltipWrapper text="Next" position="bottom">
              <button
                :disabled="!canGoNext"
                :aria-disabled="!canGoNext"
                class="btn btn-secondary btn-icon btn-sm"
                aria-label="Next fighters"
                @click="nextSlide"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </TooltipWrapper>
          </div>
        </div>
        <!-- Fighters Loading Skeleton -->
        <div v-if="fightersLoading && visibleFighters.length === 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="group" aria-roledescription="slide">
          <FighterCardSkeleton v-for="n in fightersPerPage" :key="n" />
        </div>
        <!-- Fighters Content -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="group" aria-roledescription="slide" :aria-label="`Slide ${currentSlide} of ${totalSlides}`">
          <RouterLink
            v-for="fighter in visibleFighters"
            :key="fighter.id"
            :to="{ name: 'fighter-detail', params: { id: fighter.id } }"
            class="card card-interactive hover:ring-2 hover:ring-ufc-gold transition-all"
          >
            <div class="aspect-square bg-gray-700 rounded-lg mb-4 overflow-hidden">
              <FighterImage
                :src="fighter.imageUrl"
                :alt="fighter.name"
              />
            </div>
            <h3 class="text-h4 text-white">{{ fighter.name }}</h3>
            <p class="text-ufc-gold-accessible text-sm mb-2">"{{ fighter.nickname }}"</p>
            <div class="flex flex-wrap gap-2">
              <BadgeComponent variant="danger">{{ fighter.weightClass }}</BadgeComponent>
              <BadgeComponent variant="neutral">{{ fighter.record }}</BadgeComponent>
            </div>
          </RouterLink>
        </div>

        <!-- Pagination Dots -->
        <div
          v-if="!fightersLoading && totalSlides > 1"
          class="flex justify-center items-center mt-4"
          role="tablist"
          aria-label="Carousel pagination"
        >
          <button
            v-for="(_, index) in totalSlides"
            :key="index"
            role="tab"
            :aria-selected="index === carouselIndex"
            :aria-label="`Go to slide ${index + 1} of ${totalSlides}`"
            class="min-w-[44px] min-h-[44px] flex items-center justify-center focus-ring rounded-full group"
            @click="goToSlide(index)"
          >
            <span
              :class="[
                'w-6 h-6 rounded-full transition-all duration-200',
                index === carouselIndex
                  ? 'bg-ufc-gold scale-110'
                  : 'bg-gray-600 group-hover:bg-gray-500 group-hover:scale-125'
              ]"
            />
          </button>
        </div>

        <!-- Keyboard navigation instructions -->
        <p v-if="!fightersLoading && totalSlides > 1" class="text-center text-sm text-gray-400 mt-2">
          Use arrow keys to navigate
        </p>

        <div class="text-center mt-4">
          <RouterLink :to="{ name: 'fighters' }" class="btn btn-outline">
            View All Fighters
          </RouterLink>
        </div>
      </section>

      <!-- Recent Results -->
      <section v-if="!eventsError" aria-labelledby="results-heading">
        <h2 id="results-heading" class="text-h2 text-white section-gap-sm">Recent Results</h2>
        <!-- Results Loading Skeleton -->
        <div v-if="eventsLoading && recentResults.length === 0" class="space-y-4">
          <EventCardSkeleton v-for="n in 3" :key="n" />
        </div>
        <!-- Results Content -->
        <div v-else-if="recentResults.length > 0" class="space-y-4">
          <RouterLink
            v-for="event in recentResults"
            :key="event.id"
            :to="{ name: 'event-detail', params: { id: event.id } }"
            class="card card-interactive hover:ring-2 hover:ring-ufc-gold transition-all block"
          >
            <div class="flex flex-col md:flex-row md:items-center gap-4">
              <div class="md:w-24">
                <StatusIndicator :status="event.status" />
              </div>
              <div class="flex-1">
                <h3 class="text-h4 text-white">{{ event.name }}</h3>
                <p class="text-ufc-gold-accessible font-semibold">{{ event.mainEvent }}</p>
                <!-- Show main event result if available -->
                <div v-if="event.fights[0]?.result" class="mt-2 text-sm text-gray-300">
                  <span class="text-green-400 font-semibold">
                    {{ event.fights[0].result.winner === 'fighter1' ? event.fights[0].fighter1 : event.fights[0].fighter2 }}
                  </span>
                  wins by {{ event.fights[0].result.method }} (Round {{ event.fights[0].result.round }})
                </div>
              </div>
              <div class="text-gray-300 text-sm">
                {{ formatDate(event.date) }}
              </div>
            </div>
          </RouterLink>
        </div>
        <!-- No Results Message -->
        <EmptyState
          v-else
          title="No recent results available"
          description="Check back after upcoming events are completed"
          icon="events"
        />
        <div class="text-center mt-4">
          <RouterLink :to="{ name: 'events' }" class="btn btn-outline">
            View All Events
          </RouterLink>
        </div>
      </section>
    </div>
  </div>
</template>
